import { useCallback, useEffect, useState } from "react";
import { fetchAttendingEvents } from "store/attendingEvents/attendingEvents.actions";
import { AttendingEventType } from "store/attendingEvents/attendingEvents.types";
import { useAppDispatch, useAppSelector } from "store/hooks";
import AttendingEventItem from "../AttendingEventItem";
import * as S from "./index.styles";

enum SeeStates {
  onSeeAllState = "See less events",
  onSeeLessState = "See all events",
}

const AttendingEventsSection: React.FC = () => {
  const {
    data: attendingEvents,
    loading,
    firstPage,
    currentPage,
  } = useAppSelector((state) => state.attendingEvents);
  const { current_page, total_pages } = currentPage.pagination;
  const [seeState, setSeeState] = useState(SeeStates.onSeeLessState);
  const dispatch = useAppDispatch();
  const [seeAll, setSeeAll] = useState(false);
  const [displayedAttendingEvents, setDisplayedAttendingEvents] = useState(attendingEvents);
  const VISIBLE_EVENTS = 3;

  // Filter out refused events from firstPage
  const filteredFirstPage = firstPage.filter(
    event => event.type !== AttendingEventType.REFUSED
  );

  const updateDisplayedEvents = useCallback(() => {
    if (!seeAll) {
      setDisplayedAttendingEvents(filteredFirstPage);
    } else {
      setDisplayedAttendingEvents(attendingEvents);
    }
  }, [seeAll, filteredFirstPage, attendingEvents]);

  useEffect(() => {
    updateDisplayedEvents();
  }, [updateDisplayedEvents]);

  const onSeeAll = useCallback(() => {
    const newSeeAllState = !seeAll;
    setSeeAll(newSeeAllState);
    setSeeState(
      newSeeAllState ? SeeStates.onSeeAllState : SeeStates.onSeeLessState
    );
  }, [seeAll]);

  // Initial fetch
  useEffect(() => {
    dispatch(fetchAttendingEvents({ page: 1, limit: VISIBLE_EVENTS }));
  }, [dispatch]);

  // Infinite scroll
  useEffect(() => {
    if (seeAll && currentPage.data.length > 0 && current_page < total_pages) {
      const container = document.querySelector(".attending-events-container");
      if (container) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !loading) {
                dispatch(
                  fetchAttendingEvents({
                    page: current_page + 1,
                    limit: VISIBLE_EVENTS,
                  })
                );
              }
            });
          },
          { threshold: 0.5 }
        );

        const sentinel = container.querySelector(".sentinel-loader");
        if (sentinel) {
          observer.observe(sentinel);
        }

        return () => {
          observer.disconnect();
        };
      }
    }
  }, [seeAll, current_page, loading, total_pages, dispatch]);

  return (
    <S.AttendingEventsSection>
      <S.SectionTitle>Attending Events</S.SectionTitle>
      <S.AttendingEventsContainer
        className="attending-events-container"
        $length={displayedAttendingEvents.length}
        $seeAll={seeAll}
        $visibleEvents={VISIBLE_EVENTS}
      >
        {displayedAttendingEvents?.map((attendingEvent) => (
          <AttendingEventItem
            key={attendingEvent.id}
            attendingEvent={attendingEvent}
          />
        ))}
        {seeAll && displayedAttendingEvents.length > 0 && current_page < total_pages && (
          <div
            className="sentinel-loader"
            style={{ height: "0.625rem", width: "100%", margin: "0.625rem 0" }}
          ></div>
        )}
      </S.AttendingEventsContainer>
      {displayedAttendingEvents.length > 0 && (
        <S.ShowMoreLink onClick={onSeeAll}>{seeState}</S.ShowMoreLink>
      )}
    </S.AttendingEventsSection>
  );
};

export default AttendingEventsSection;
